/**
 * News Parser — normalizes RSS/JSON feed items to the app's NewsArticle schema.
 * --------------------------------------------------------------------------
 * Handles:
 *   - RSS 2.0, Atom, JSON Feed via rss2json.com normalized output
 *   - Field mapping & fallbacks
 *   - De-duplication by URL + title similarity
 *   - Category inference from tags/keywords
 *   - Breaking news detection
 *   - Image extraction (media:content, enclosure, content:encoded)
 */

import { getEnabledSources, CATEGORY_KEYWORDS, TEAM_COLORS, FETCH_CONFIG } from '../data/newsSources.js';
import { cleanHtml } from './htmlText.js';

const DRIVER_KEYWORDS = [
  'verstappen', 'hamilton', 'leclerc', 'norris', 'piastri', 'russell', 'sainz',
  'alonso', 'ocon', 'gasly', 'tsunoda', 'albon', 'sargeant', 'ricciardo', 'lawson',
  'hadjar', 'bortoleto', 'antonelli', 'bearman', 'doohan', 'colapinto', 'hulkenberg',
  'magnussen', 'stroll', 'bottas', 'zhou', 'perez',
];

const DEFAULT_ACCENT = '#FF8000';

/**
 * Generate a stable ID for an article
 */
function generateId(sourceId, item) {
  const base = item.guid || item.link || item.url || item.title || '';
  let hash = 0;
  for (let i = 0; i < base.length; i++) {
    const char = base.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return `${sourceId}-${Math.abs(hash).toString(36)}`;
}

/**
 * Parse date string to ISO 8601. Returns null when there is no usable date,
 * so callers can distinguish "unknown" from "just published".
 */
function parseDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Formula1.com's RSS omits <pubDate> on every item, and rss2json exposes no
 * feed-level lastBuildDate to fall back on. Stamping `Date.now()` (the old
 * behaviour) gave all ten items an identical "now" timestamp, so they swept
 * the entire top of the merged feed — hero card included — every one of them
 * labelled "JUST NOW".
 *
 * RSS convention is newest-first, so item order is still meaningful. We
 * synthesise a descending timestamp to preserve that order and keep the sort
 * stable, but flag it `dateEstimated` so the UI shows "Recent" rather than
 * asserting a freshness we cannot actually verify.
 */
const ESTIMATED_DATE_SPACING_MS = 20 * 60 * 1000;
const ESTIMATED_DATE_BACKDATE_MS = 45 * 60 * 1000;

function estimatedDate(index) {
  const anchor = Date.now() - ESTIMATED_DATE_BACKDATE_MS;
  return new Date(anchor - index * ESTIMATED_DATE_SPACING_MS).toISOString();
}

/**
 * Extract image URL from RSS item
 */
function extractImage(item) {
  // rss2json puts images in these fields
  if (item.enclosure?.url && item.enclosure.type?.startsWith('image/')) {
    return item.enclosure.url;
  }
  if (item.media?.content?.length) {
    const img = item.media.content.find((m) => m.type?.startsWith('image/'));
    if (img) return img.url;
  }
  if (item['media:thumbnail']?.url) return item['media:thumbnail'].url;
  if (item.thumbnail) return item.thumbnail;

  // Check content/description for img tags
  const content = item.content || item['content:encoded'] || item.description || '';
  const imgMatch = content.match(/<img[^>]+src=["']([^"']+)["']/i);
  if (imgMatch) return imgMatch[1];

  return null;
}

/**
 * Entity decoding and tag stripping now live in utils/htmlText.js, shared with
 * the article extractor — see the note at the top of that file.
 */

/**
 * Truncate string with ellipsis
 */
function truncate(str, maxLen) {
  if (!str) return '';
  if (str.length <= maxLen) return str;
  const truncated = str.slice(0, maxLen).trim();
  const lastSpace = truncated.lastIndexOf(' ');
  return lastSpace > maxLen * 0.7 ? truncated.slice(0, lastSpace) + '…' : truncated + '…';
}

/**
 * Infer category from content
 */
/** Minimum evidence before overriding the source's own default category. */
const CATEGORY_MIN_SCORE = 3;

/**
 * inferCategory — weighted keyword scoring.
 *
 * The title carries far more signal than the body, and a multi-word phrase
 * ("championship lead") is much stronger evidence than a bare word. The old
 * implementation counted every keyword equally across title+description, which
 * let generic tokens dominate: 42 of 60 articles landed in "race" and only 3
 * in "feature".
 *
 * `breaking` is skipped — it is surfaced through the isBreaking flag, and
 * assigning it as a category made articles unreachable from every filter chip.
 */
function inferCategory(item, sourceCategory) {
  const title = (item.title || '').toLowerCase();
  const body = `${item.description || ''} ${item.categories?.join(' ') || ''}`.toLowerCase();

  const scores = {};
  for (const [cat, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    if (cat === 'breaking') continue;
    let score = 0;
    for (const kw of keywords) {
      const weight = kw.includes(' ') ? 3 : 2;
      if (title.includes(kw)) score += weight * 2; // title hits count double
      else if (body.includes(kw)) score += weight;
    }
    scores[cat] = score;
  }

  const [topCat, topScore] = Object.entries(scores).sort((a, b) => b[1] - a[1])[0] ?? [];
  if (topScore >= CATEGORY_MIN_SCORE) return topCat;

  return sourceCategory || 'feature';
}

/**
 * Detect breaking news
 */
function detectBreaking(item) {
  const text = `${item.title || ''} ${item.description || ''}`.toLowerCase();
  return CATEGORY_KEYWORDS.breaking.some((kw) => text.includes(kw));
}

/**
 * Infer accent color from team/driver mentions
 */
function inferAccentColor(item, sourceColor) {
  const text = `${item.title || ''} ${item.description || ''} ${item.categories?.join(' ') || ''}`.toLowerCase();

  for (const [team, color] of Object.entries(TEAM_COLORS)) {
    if (text.includes(team)) return color;
  }

  for (const driver of DRIVER_KEYWORDS) {
    if (text.includes(driver)) {
      if (['verstappen'].includes(driver)) return TEAM_COLORS.redbull;
      if (['hamilton', 'russell', 'antonelli'].includes(driver)) return TEAM_COLORS.mercedes;
      if (['leclerc', 'sainz'].includes(driver)) return TEAM_COLORS.ferrari;
      if (['norris', 'piastri'].includes(driver)) return TEAM_COLORS.mclaren;
      if (['alonso', 'stroll'].includes(driver)) return TEAM_COLORS.astonmartin;
      if (['ocon', 'gasly', 'doohan'].includes(driver)) return TEAM_COLORS.alpine;
      if (['albon', 'sargeant', 'colapinto'].includes(driver)) return TEAM_COLORS.williams;
      if (['tsunoda', 'lawson', 'hadjar'].includes(driver)) return TEAM_COLORS.rb;
      if (['hulkenberg', 'magnussen', 'bortoleto'].includes(driver)) return TEAM_COLORS.haas;
      if (['bottas', 'zhou'].includes(driver)) return TEAM_COLORS.sauber;
    }
  }

  return sourceColor || DEFAULT_ACCENT;
}

/**
 * Estimate read time in minutes
 */
function estimateReadTime(text) {
  const words = (text || '').split(/\s+/).filter(Boolean).length;
  const minutes = Math.ceil(words / 200);
  return Math.max(1, Math.min(minutes, 15));
}

/**
 * Normalize a single RSS item to NewsArticle
 */
export function normalizeArticle(source, item, index = 0) {
  const title = cleanHtml(item.title || '').trim();
  const description = cleanHtml(item.description || item.content || item['content:encoded'] || '').trim();
  const link = item.link || item.guid || item.url || '';

  if (!title || !link) return null;

  const realDate = parseDate(item.pubDate || item.published || item.updated);

  // Richest body the feed offers. Most feeds put a 100–400 char teaser in both
  // description and content, but The Race ships the entire article in
  // <content:encoded> — which the reader can then show with no network call.
  // Keep the markup: the reader needs paragraph boundaries, images and links,
  // all of which cleanHtml() flattens away.
  const contentHtml = [item['content:encoded'], item.content, item.description]
    .filter((candidate) => typeof candidate === 'string')
    .sort((a, b) => b.length - a.length)[0] ?? '';

  return {
    id: generateId(source.id, item),
    category: inferCategory(item, source.category),
    title: truncate(title, 120),
    summary: truncate(description, 300),
    contentHtml,
    coverImage: extractImage(item),
    accentColor: inferAccentColor(item, source.accentColor),
    publishedAt: realDate ?? estimatedDate(index),
    dateEstimated: realDate === null,
    // Measured on the fullest text available, not on `description`. Keying it
    // off the teaser labelled every one of The Race's ~1,000-word features
    // "1 min read".
    readTime: estimateReadTime(cleanHtml(contentHtml) || description),
    source: source.name,
    sourceId: source.id,
    isBreaking: detectBreaking(item),
    tags: item.categories?.map((c) => cleanHtml(c).trim()).filter(Boolean) || [],
    originalUrl: link,
  };
}

/**
 * Fetch and parse a single RSS feed via rss2json proxy
 */
export async function fetchAndNormalizeFeed(source, signal) {
  const { baseUrl, params } = FETCH_CONFIG.rss2json;
  const urlParams = new URLSearchParams({ rss_url: source.url });
  // Only attach `count`/`api_key` when explicitly configured — rss2json's
  // free tier rejects requests that pass `count` without a valid api_key.
  if (params.api_key) {
    urlParams.set('api_key', params.api_key);
    if (params.count) urlParams.set('count', String(params.count));
  }

  const url = `${baseUrl}?${urlParams.toString()}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_CONFIG.timeoutMs);

  if (signal) {
    signal.addEventListener('abort', () => controller.abort());
  }

  try {
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.status !== 'ok' || !data.items?.length) {
      console.warn(`Feed ${source.id}: ${data.status} - ${data.message || 'No items'}`);
      return { source, data: [] };
    }

    const articles = data.items
      .map((item, index) => normalizeArticle(source, item, index))
      .filter(Boolean);

    return { source, data: articles };
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error(`Feed ${source.id} timed out`, { cause: error });
    }
    throw error;
  }
}

/**
 * De-duplicate articles by URL + title similarity
 */
export function deduplicateArticles(articles) {
  const seen = new Map();
  const unique = [];

  for (const article of articles) {
    const key = article.originalUrl || article.title.toLowerCase().slice(0, 80);

    if (seen.has(key)) {
      const existing = seen.get(key);
      if (article.publishedAt > existing.publishedAt) {
        seen.set(key, article);
      }
    } else {
      seen.set(key, article);
      unique.push(article);
    }
  }

  return unique;
}

/**
 * reconcileEstimatedDates — re-anchor synthesised dates against real ones.
 *
 * normalizeArticle can only see one feed at a time, so an undated article gets
 * a placeholder near "now". Once every feed is merged we can do better: an
 * article with no date is not evidence of freshness, yet a near-now placeholder
 * outranks every genuinely-dated article and hands one source the entire first
 * screen (measured: 10 of the top 10 slots).
 *
 * So anchor undated articles at the MEDIAN of the dates we actually know,
 * preserving their in-feed order. Unknown recency is treated as typical rather
 * than best-in-class, and they interleave instead of dominating.
 *
 * Call this after merging feeds and before sorting. No-ops when every article
 * has a real date, or when none do.
 */
export function reconcileEstimatedDates(articles) {
  const known = articles
    .filter((a) => !a.dateEstimated)
    .map((a) => new Date(a.publishedAt).getTime())
    .filter(Number.isFinite)
    .sort((a, b) => a - b);

  if (known.length === 0) return articles;

  const median = known[Math.floor(known.length / 2)];
  const estimated = articles.filter((a) => a.dateEstimated);
  if (estimated.length === 0) return articles;

  // Preserve each feed's own ordering while re-anchoring.
  const seen = new Map();
  return articles.map((a) => {
    if (!a.dateEstimated) return a;
    const rank = seen.get(a.source) ?? 0;
    seen.set(a.source, rank + 1);
    return {
      ...a,
      publishedAt: new Date(median - rank * ESTIMATED_DATE_SPACING_MS).toISOString(),
    };
  });
}

/**
 * Sort articles by date descending (freshest first). Ties break on source name
 * then id so the order is deterministic across renders.
 */
export function sortArticles(articles) {
  return [...articles].sort((a, b) => {
    const diff = new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
    if (diff !== 0) return diff;
    return (a.source ?? '').localeCompare(b.source ?? '') || (a.id ?? '').localeCompare(b.id ?? '');
  });
}

/**
 * Filter articles by category
 */
export function filterByCategory(articles, categoryId) {
  if (!categoryId || categoryId === 'all') return articles;
  if (categoryId === 'breaking') return articles.filter((a) => a.isBreaking);
  return articles.filter((a) => a.category === categoryId);
}

/**
 * Batch fetch all enabled sources
 */
export async function fetchAllFeeds(signal) {
  const sources = getEnabledSources();

  const results = await Promise.allSettled(
    sources.map((source) => fetchAndNormalizeFeed(source, signal))
  );

  const allArticles = results
    .filter((r) => r.status === 'fulfilled')
    .flatMap((r) => r.value.data);

  const errors = results
    .filter((r) => r.status === 'rejected')
    .map((r) => r.reason);

  return { articles: allArticles, errors };
}